import { useState, type ReactNode } from 'react';
import {
  Toolbar,
  SaveButton,
  DeleteButton,
  usePermissions,
  useRecordContext,
  Form,
  type FormProps,
} from 'react-admin';
import { Box, ButtonBase, Typography } from '@mui/material';
import { hasRole, type Permissions } from '../types';

interface TabbedFormProps extends Omit<FormProps, 'children'> {
  labels: string[];
  children: ReactNode[];
  /** Replaces the default toolbar when provided. */
  toolbar?: ReactNode;
}

export const TabbedForm = ({ labels, children, toolbar, ...props }: TabbedFormProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const { permissions } = usePermissions<Permissions>();
  const record = useRecordContext();

  return (
    <Form {...props}>
      <Box display="flex" minHeight={400}>
        <Box
          display="flex"
          flexDirection="column"
          p={2}
          minWidth={160}
          sx={{ borderRight: '1px solid', borderColor: 'divider' }}
        >
          {labels.map((label, i) => (
            <ButtonBase
              key={label}
              onClick={() => setTabIndex(i)}
              sx={{
                p: '10px 20px',
                justifyContent: 'flex-start',
                borderRadius: 1,
                backgroundColor:
                  tabIndex === i ? 'action.selected' : 'transparent',
              }}
            >
              <Typography
                sx={{ fontWeight: tabIndex === i ? 700 : 400 }}
              >
                {label}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
        <Box flex={1} p={3}>
          {children.map((page, i) => (
            <Box
              key={i}
              sx={{ display: tabIndex === i ? 'block' : 'none' }}
            >
              <Typography variant="h6" gutterBottom>
                {labels[i]}
              </Typography>
              {page}
            </Box>
          ))}
        </Box>
      </Box>

      {toolbar ?? (
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <SaveButton />
          <Box>
            <SaveButton
              label={record?.archived_at ? 'Unarchive' : 'Archive'}
              variant="text"
              type="button"
              transform={(data: any) => ({
                ...data,
                archived_at: record?.archived_at
                  ? null
                  : new Date().toISOString(),
              })}
            />
            {hasRole(permissions, 'tenant_admin') && <DeleteButton />}
          </Box>
        </Toolbar>
      )}
    </Form>
  );
};
